import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ExternalServiceProvider,
  ExternalServiceProviderCreate,
  ExternalServiceProviderDocument,
  ExternalServiceProviderListResponse,
  ExternalServiceProviderUpdate
} from '../models/ExternalServiceProvider';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ExternalServiceProvidersService {
  private readonly baseUrl = environment.microservices.contract;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  list(
    pageNumber = 1,
    pageSize = 10,
    searchTerm?: string,
    servedUf?: string,
    serviceType?: string
  ): Observable<{ items: ExternalServiceProvider[]; pagination: ExternalServiceProviderListResponse['pagination'] }> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (searchTerm && searchTerm.trim()) {
      params = params.set('searchTerm', searchTerm.trim());
    }
    if (servedUf && servedUf.trim()) {
      params = params.set('servedUf', servedUf.trim().toUpperCase());
    }
    if (serviceType && serviceType.trim()) {
      params = params.set('serviceType', serviceType.trim());
    }

    const url = `${this.baseUrl}/list/external-service-providers`;

    return this.http.get<any>(url, { params }).pipe(
      map(response => ({
        items: (response?.data || []).map((item: any) => this.mapFromBackendDto(item)),
        pagination: response?.pagination
      })),
      catchError(err => {
        this.errorService.showError('Erro ao buscar prestadores de serviço');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<ExternalServiceProvider> {
    const url = `${this.baseUrl}/view/external-service-providers/${id}`;
    return this.http.get<any>(url).pipe(
      map(res => {
        const data = res?.data || res;
        return this.mapFromBackendDto(data);
      }),
      catchError(err => {
        this.errorService.showError('Erro ao buscar prestador de serviço');
        return throwError(() => err);
      })
    );
  }

  create(payload: ExternalServiceProviderCreate): Observable<ExternalServiceProvider> {
    const url = `${this.baseUrl}/create/external-service-providers`;
    return this.http.post<any>(url, payload).pipe(
      map(res => {
        const data = res?.data || res;
        return this.mapFromBackendDto(data);
      }),
      catchError(err => {
        this.errorService.showError('Erro ao criar prestador de serviço');
        return throwError(() => err);
      })
    );
  }

  createWithFiles(formData: FormData): Observable<ExternalServiceProvider> {
    const url = `${this.baseUrl}/create/external-service-providers`;
    // NÃO adiciona Content-Type manualmente - o Angular/FormData define automaticamente com boundary
    // eslint-disable-next-line no-console
    return this.http.post<any>(url, formData).pipe(
      map(res => {
        const data = res?.data || res;
        return this.mapFromBackendDto(data);
      }),
      catchError(err => {
        this.errorService.showError('Erro ao criar prestador de serviço com arquivos');
        return throwError(() => err);
      })
    );
  }

  update(id: number, payload: ExternalServiceProviderUpdate): Observable<void> {
    const url = `${this.baseUrl}/edit/external-service-providers/${id}`;
    return this.http.put<void>(url, payload).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao atualizar prestador de serviço');
        return throwError(() => err);
      })
    );
  }

  updateWithFiles(id: number, formData: FormData): Observable<ExternalServiceProvider> {
    const url = `${this.baseUrl}/edit/external-service-providers/${id}`;
    // NÃO adiciona Content-Type manualmente - o Angular/FormData define automaticamente com boundary
    // eslint-disable-next-line no-console
    return this.http.put<any>(url, formData).pipe(
      map(res => {
        const data = res?.data || res;
        return this.mapFromBackendDto(data);
      }),
      catchError(err => {
        this.errorService.showError('Erro ao atualizar prestador de serviço com arquivos');
        return throwError(() => err);
      })
    );
  }

  /** UF operacional do veículo B2B (item/snapshot/placa) para filtrar parceiros. */
  resolveVehicleState(params: {
    plate?: string | null;
    contractId?: number | null;
    snapshotId?: number | null;
    itemId?: number | null;
    vehicleId?: number | null;
  }): Observable<string | null> {
    let httpParams = new HttpParams();
    if (params.plate?.trim()) httpParams = httpParams.set('plate', params.plate.trim());
    if (params.contractId && params.contractId > 0) httpParams = httpParams.set('contractId', String(params.contractId));
    if (params.snapshotId && params.snapshotId > 0) httpParams = httpParams.set('snapshotId', String(params.snapshotId));
    if (params.itemId && params.itemId > 0) httpParams = httpParams.set('itemId', String(params.itemId));
    if (params.vehicleId && params.vehicleId > 0) httpParams = httpParams.set('vehicleId', String(params.vehicleId));

    return this.http.get<any>(`${this.baseUrl}/b2b/vehicle-state`, { params: httpParams }).pipe(
      map(res => {
        const state = res?.data?.state ?? res?.state ?? null;
        return typeof state === 'string' && state.trim() ? state.trim().toUpperCase() : null;
      }),
      catchError(err => {
        console.error('Erro ao resolver UF do veículo B2B:', err);
        return throwError(() => err);
      })
    );
  }

  delete(id: number): Observable<void> {
    const url = `${this.baseUrl}/delete/external-service-providers/${id}`;
    return this.http.delete<void>(url).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao excluir prestador de serviço');
        return throwError(() => err);
      })
    );
  }

  /**
   * Mapeia o DTO do backend (PascalCase) para o modelo do frontend (camelCase)
   */
  private mapFromBackendDto(dto: any): ExternalServiceProvider {
    return {
      id: dto?.Id || dto?.id || 0,
      tradeName: dto?.TradeName || dto?.tradeName || '',
      companyName: dto?.CompanyName || dto?.companyName || '',
      cnpj: dto?.Cnpj || dto?.cnpj || '',
      fullAddress: dto?.FullAddress || dto?.fullAddress,
      phone: dto?.Phone || dto?.phone,
      contactEmail: dto?.ContactEmail || dto?.contactEmail,
      responsibleName: dto?.ResponsibleName || dto?.responsibleName,
      serviceType: dto?.ServiceType || dto?.serviceType,
      servedStates: dto?.ServedStates || dto?.servedStates || dto?.ServedRegions || dto?.servedRegions,
      supportedVehicleTypes: dto?.SupportedVehicleTypes || dto?.supportedVehicleTypes,
      businessHours: dto?.BusinessHours || dto?.businessHours,
      pixKey: dto?.PixKey || dto?.pixKey || '',
      documents: this.mapDocumentsFromBackendDto(dto?.Documents || dto?.documents || []),
      createdAt: dto?.CreatedAt || dto?.createdAt,
      updatedAt: dto?.UpdatedAt || dto?.updatedAt
    };
  }

  /**
   * Mapeia o array de documentos do backend (PascalCase) para o modelo do frontend (camelCase)
   */
  private mapDocumentsFromBackendDto(documents: any[]): ExternalServiceProviderDocument[] {
    if (!Array.isArray(documents)) {
      return [];
    }

    return documents.map(doc => ({
      id: doc?.Id || doc?.id,
      documentName: doc?.DocumentName || doc?.documentName,
      documentType: doc?.DocumentType || doc?.documentType,
      documentUrl: doc?.DocumentUrl || doc?.documentUrl
    }));
  }
}


